<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGeoStore } from '@/stores/geo'
import { MapPinned, FolderOpen, ListTree } from 'lucide-vue-next'
import MetricChart from '@/pages/Project/Monitoring/MetricChart.vue'
import ChartCard from './ChartCard.vue'
import ChatAvatar from './ChatAvatar.vue'

const props = defineProps({
  attachment: { type: Object, required: true },
})

const emit = defineEmits(['open'])

const route = useRoute()
const router = useRouter()
const geoStore = useGeoStore()

const series = computed(() => [
  { label: props.attachment.title, points: props.attachment.points || [], color: props.attachment.color },
])
const xMin = computed(() => props.attachment.points?.[0]?.x ?? 0)
const xMax = computed(() => props.attachment.points?.[props.attachment.points.length - 1]?.x ?? 1)

// "Jan 2018 – Jul 2026" footer for chart cards; absent on old cards.
const dateRange = computed(() => {
  const { date_from: from, date_to: to } = props.attachment
  if (!from || !to) return null
  const fmt = (d) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return `${fmt(from)} – ${fmt(to)}`
})

const insightSeries = computed(() =>
  (props.attachment.series || []).map((s) => ({
    label: s.label,
    points: s.points || [],
    color: s.color,
  }))
)
const insightXMin = computed(() =>
  Math.min(...insightSeries.value.map((s) => s.points[0]?.x ?? 0))
)
const insightXMax = computed(() =>
  Math.max(...insightSeries.value.map((s) => s.points[s.points.length - 1]?.x ?? 1))
)

// Cards carry their own org/project identity (agent-built and unfurled cards
// always do); older attachments fall back to the current route's params so
// they keep working inside their project.
const orgId = computed(() => props.attachment.org_id ?? route.params.org_id)
const projectId = computed(() => props.attachment.project_id ?? route.params.project_id)

const memberAuthor = computed(() => ({
  member_id: props.attachment.member_id,
  display_name: props.attachment.display_name,
  avatar_url: props.attachment.avatar_url,
}))

function pushProjectRoute(name, extra = {}) {
  if (!orgId.value || !projectId.value) return
  router.push({
    name,
    params: { org_id: orgId.value, project_id: projectId.value, ...(extra.params || {}) },
    query: extra.query || {},
  })
}

function openMetric() {
  pushProjectRoute('ProjectMonitoring', { query: { metric: props.attachment.metric } })
}

function openFeatureOnMap(featureCode) {
  // Map popup with the feature's geometry when the map is around (project
  // pages); otherwise navigate to the feature page.
  if (geoStore.map) {
    geoStore.openFeaturePopupByCode(featureCode)
    return
  }
  pushProjectRoute('FeatureIndex', { params: { featureCode } })
}

function openMember() {
  pushProjectRoute('ProjectMembers')
}

function openProject() {
  const targetOrg = props.attachment.org_id ?? route.params.org_id
  if (!targetOrg) return
  router.push({
    name: 'Project',
    params: { org_id: targetOrg, project_id: props.attachment.project_id },
  })
}
</script>

<template>
  <button
    v-if="attachment.type === 'metric'"
    class="w-full min-w-[180px] max-w-[220px] rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/50"
    title="Open in Monitoring"
    @click.stop="openMetric"
  >
    <div class="truncate text-xs font-medium text-muted-foreground">{{ attachment.title }}</div>
    <div class="flex items-baseline gap-1">
      <span class="text-base font-bold leading-tight text-foreground">{{ attachment.value }}</span>
      <span v-if="attachment.unit" class="text-[10px] text-muted-foreground">{{ attachment.unit }}</span>
    </div>
    <div class="mt-0.5 h-10 border-b border-border/40">
      <MetricChart :series="series" :x-min="xMin" :x-max="xMax" sparkline />
    </div>
    <div v-if="dateRange" class="mt-1 text-[10px] text-muted-foreground">{{ dateRange }}</div>
  </button>

  <button
    v-else-if="attachment.type === 'image'"
    class="block overflow-hidden rounded-md border bg-card transition-colors hover:border-primary/50"
    :title="attachment.filename"
    @click.stop="emit('open')"
  >
    <img
      :src="attachment.variant_urls?.thumbnail || attachment.url"
      :alt="attachment.filename"
      class="size-24 object-cover"
    />
  </button>

  <button
    v-else-if="attachment.type === 'member'"
    class="flex w-full min-w-[180px] max-w-[240px] items-center gap-2 rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/50"
    title="Open project members"
    @click.stop="openMember"
  >
    <ChatAvatar :author="memberAuthor" size="base" />
    <div class="min-w-0">
      <div class="truncate text-sm font-medium">{{ attachment.display_name }}</div>
      <div v-if="attachment.role" class="truncate text-xs text-muted-foreground">{{ attachment.role }}</div>
    </div>
  </button>

  <button
    v-else-if="attachment.type === 'project'"
    class="flex w-full min-w-[180px] max-w-[260px] items-center gap-2 rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/50"
    title="Open project"
    @click.stop="openProject"
  >
    <span class="text-lg">{{ attachment.emoji || '📁' }}</span>
    <div class="min-w-0">
      <div class="truncate text-sm font-medium">{{ attachment.name }}</div>
      <div v-if="attachment.description" class="truncate text-xs text-muted-foreground">
        {{ attachment.description }}
      </div>
    </div>
    <FolderOpen class="ml-auto size-3.5 shrink-0 text-muted-foreground" />
  </button>

  <button
    v-else-if="attachment.type === 'feature'"
    class="flex w-full min-w-[180px] max-w-[260px] items-center gap-2 rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/50"
    title="Show on map"
    @click.stop="openFeatureOnMap(attachment.feature_code)"
  >
    <MapPinned class="size-4 shrink-0 text-primary" />
    <div class="min-w-0">
      <div class="truncate text-sm font-medium">{{ attachment.name }}</div>
      <div class="truncate text-xs text-muted-foreground">
        {{ [attachment.feature_type, attachment.date].filter(Boolean).join(' · ') }}
      </div>
    </div>
  </button>

  <button
    v-else-if="attachment.type === 'insight'"
    class="w-full min-w-[240px] max-w-[300px] rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/50"
    title="Open in Monitoring"
    @click.stop="pushProjectRoute('ProjectMonitoring', { query: { metric: attachment.series?.[0]?.metric } })"
  >
    <div class="truncate text-xs font-medium text-muted-foreground">
      {{ attachment.title || 'Combined trends' }}
    </div>
    <div class="mt-1 h-16 border-b border-border/40">
      <MetricChart :series="insightSeries" :x-min="insightXMin" :x-max="insightXMax" sparkline />
    </div>
    <div class="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10px] text-muted-foreground">
      <span
        v-for="s in insightSeries"
        :key="s.label"
        class="inline-flex items-center gap-1"
      >
        <span class="size-1.5 rounded-full" :style="{ background: s.color }"></span>
        {{ s.label }}
      </span>
      <span v-if="dateRange" class="ml-auto">{{ dateRange }}</span>
    </div>
    <div class="text-[10px] italic text-muted-foreground/70">relative trends</div>
  </button>

  <div
    v-else-if="attachment.type === 'chart'"
    class="w-full min-w-[240px] max-w-[300px] rounded-lg border bg-card p-2"
  >
    <div class="truncate text-xs font-medium text-muted-foreground">{{ attachment.title }}</div>
    <div class="mt-1" :class="attachment.kind === 'pie' ? 'h-40' : 'h-24'">
      <ChartCard :attachment="attachment" />
    </div>
    <div v-if="dateRange" class="mt-1 text-[10px] text-muted-foreground">{{ dateRange }}</div>
  </div>

  <div
    v-else-if="attachment.type === 'table'"
    class="w-full min-w-[200px] max-w-[300px] rounded-lg border bg-card p-2"
  >
    <div class="mb-1 truncate text-xs font-medium text-muted-foreground">{{ attachment.title }}</div>
    <table class="w-full text-xs">
      <thead>
        <tr>
          <th
            v-for="(col, ci) in attachment.columns"
            :key="col"
            class="border-b border-border/40 pb-1 font-medium text-muted-foreground"
            :class="ci === 0 ? 'text-left' : 'text-right'"
          >
            {{ col }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, ri) in attachment.rows" :key="ri">
          <td
            v-for="(cell, ci) in row"
            :key="ci"
            class="py-0.5"
            :class="ci === 0 ? 'truncate text-left' : 'text-right tabular-nums'"
          >
            {{ cell }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div
    v-else-if="attachment.type === 'feature_list'"
    class="w-full min-w-[200px] max-w-[280px] rounded-lg border bg-card p-2"
  >
    <div class="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <ListTree class="size-3.5" />
      {{ attachment.title || `Features (${attachment.items?.length || 0})` }}
    </div>
    <button
      v-for="item in attachment.items"
      :key="item.feature_code"
      class="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-muted hover:text-primary"
      title="Show on map"
      @click.stop="openFeatureOnMap(item.feature_code)"
    >
      <MapPinned class="size-3 shrink-0 text-muted-foreground" />
      <span class="truncate">{{ item.name }}</span>
    </button>
  </div>
</template>
